import { useEffect, useState } from "react";
import { DeleteOutlined, EditOutlined, ExclamationCircleOutlined, FileAddOutlined, PictureOutlined, PlusCircleOutlined } from "@ant-design/icons";
import { Button, Modal, Spin, Switch } from "antd";
import { useNavigate } from "react-router-dom";
import MainButtonComponent from "../../components/MainButtonComponent";
import {
	ActionButtonOverlay,
	Container,
	ContainerHeader,
	HeaderComponent,
} from "../../components/styled/common";
import { useVolunteersAboutContext } from "../../context/VolunteersAboutContext/VolunteersAboutContext";
import { Culture, VOLUNTEERS_ABOUT_PATH } from "../../context/VolunteersAboutContext/constants";
import {
	AboutPreview,
	AboutPreviewDescription,
	AboutPreviewImage,
	AboutPreviewText,
	AboutPreviewTitle,
	AboutStateContainer,
	AboutStateMessage,
	LanguageSwitchContainer,
} from "./styles";

// The single "Koç Gönüllüleri Hakkında" content: an invitation to add it when there is none (404), otherwise its preview
// in the chosen language with the edit and delete actions.
const VolunteersAbout: React.FC = () => {
	const navigate = useNavigate();
	const { volunteersAbout, loadState, operationStatus, getVolunteersAbout, removeVolunteersAbout } = useVolunteersAboutContext();
	const [culture, setCulture] = useState<Culture>("tr-TR");
	const [isModalVisible, setIsModalVisible] = useState(false);
	// An image that cannot be loaded shows the placeholder; another address is tried again.
	const [failedImageUrl, setFailedImageUrl] = useState<string | null>(null);

	const isFetching = operationStatus.type === "loading" && operationStatus.operation === "fetch";
	const isDeleting = operationStatus.type === "loading" && operationStatus.operation === "delete";
	const localizable = volunteersAbout?.volunteersAboutLocalizable.find(item => item.culture === culture);

	useEffect(() => {
		getVolunteersAbout();
	}, [getVolunteersAbout]);

	// The dialog closes as soon as the delete request ends, then the screen shows what is stored now: the add
	// invitation after a delete, or the content if it could not be deleted.
	const handleDelete = async () => {
		await removeVolunteersAbout();
		setIsModalVisible(false);
		await getVolunteersAbout();
	};

	const renderPreview = () => {
		if (!localizable) {
			return (
				<AboutStateContainer>
					<FileAddOutlined />
					<AboutStateMessage>Bu dilde içerik bulunmuyor.</AboutStateMessage>
				</AboutStateContainer>
			);
		}

		// Shown like on the site: without surrounding blank lines, a Windows line break counting as one.
		const description = (localizable.description ?? "").replace(/\r\n?/g, "\n").trim();
		const hasImage = Boolean(localizable.imageUrl) && localizable.imageUrl !== failedImageUrl;

		return (
			<AboutPreview aria-label="Sitede görünecek içerik">
				<AboutPreviewText>
					<AboutPreviewTitle>{localizable.title}</AboutPreviewTitle>
					{description && <AboutPreviewDescription>{description}</AboutPreviewDescription>}
				</AboutPreviewText>
				<AboutPreviewImage>
					{hasImage
						? <img src={localizable.imageUrl} alt={localizable.title} onError={() => setFailedImageUrl(localizable.imageUrl)} />
						: <PictureOutlined aria-label="Resim bulunmuyor" />}
				</AboutPreviewImage>
			</AboutPreview>
		);
	};

	const renderContent = () => {
		if (loadState === "missing") {
			return (
				<AboutStateContainer>
					<FileAddOutlined />
					<AboutStateMessage>Koç Gönüllüleri Hakkında bölümü bulunmuyor. Lütfen ekleme yapınız.</AboutStateMessage>
					<MainButtonComponent icon={<PlusCircleOutlined />} onClick={() => navigate(`${VOLUNTEERS_ABOUT_PATH}/add`)}>
						Yeni Ekle
					</MainButtonComponent>
				</AboutStateContainer>
			);
		}

		if (loadState === "error") {
			return (
				<AboutStateContainer>
					<ExclamationCircleOutlined />
					<AboutStateMessage>Koç Gönüllüleri Hakkında içeriği yüklenemedi.</AboutStateMessage>
					<MainButtonComponent type="default" onClick={getVolunteersAbout}>
						Tekrar Dene
					</MainButtonComponent>
				</AboutStateContainer>
			);
		}

		if (loadState !== "ready" || !volunteersAbout) {
			return <AboutStateContainer />;
		}

		return (
			<>
				<ContainerHeader style={{ justifyContent: "space-between" }}>
					<LanguageSwitchContainer>
						Görünüm Dili
						<Switch
							checked={culture === "en-US"}
							checkedChildren="İngilizce"
							unCheckedChildren="Türkçe"
							onChange={checked => setCulture(checked ? "en-US" : "tr-TR")}
						/>
					</LanguageSwitchContainer>
					<ActionButtonOverlay>
						<MainButtonComponent
							type="default"
							style={{ marginRight: "15px" }}
							icon={<DeleteOutlined />}
							onClick={() => setIsModalVisible(true)}
						>
							Sil
						</MainButtonComponent>
						<MainButtonComponent
							style={{ marginRight: "15px" }}
							icon={<EditOutlined />}
							onClick={() => navigate(`${VOLUNTEERS_ABOUT_PATH}/edit/${volunteersAbout.aboutId}`)}
						>
							Düzenle
						</MainButtonComponent>
					</ActionButtonOverlay>
				</ContainerHeader>
				{renderPreview()}
			</>
		);
	};

	return (
		<div>
			<HeaderComponent>Koç Gönüllüleri Hakkında</HeaderComponent>
			<Container>
				<Spin spinning={isFetching}>{renderContent()}</Spin>
			</Container>
			<Modal
				title="Koç Gönüllüleri Hakkında İçeriğini Sil"
				open={isModalVisible}
				onCancel={() => setIsModalVisible(false)}
				centered
				footer={[
					<Button key="back" type="text" onClick={() => setIsModalVisible(false)}>
						İptal
					</Button>,
					<Button key="submit" type="primary" danger loading={isDeleting} onClick={handleDelete}>
						Evet
					</Button>,
				]}
			>
				<p>
					Koç Gönüllüleri Hakkında içeriğini Türkçe ve İngilizce olarak silmek istediğinizden emin misiniz? Silinen içerik
					sitede gösterilmez.
				</p>
			</Modal>
		</div>
	);
};

export default VolunteersAbout;
